/**
 * LO GUARDADO EN EL TELÉFONO, AL ENTRAR Y AL SALIR.
 *
 *   · Al entrar alguien: se borra lo de cualquier OTRA persona que hubiera usado
 *     este aparato (un móvil guarda los datos de una sola persona) y se pide al
 *     sistema que no borre la base por falta de espacio.
 *   · Al salir (cerrar sesión, o la sesión anulada en el servidor): se borra TODO
 *     lo de esa persona —copia, consultas, respuestas, fotos, teselas— y se vacía
 *     la caché de React Query en memoria, que hasta hoy sobrevivía al cambio de
 *     cuenta en el mismo aparato.
 */
import type { QueryClient } from "@tanstack/react-query";
import { borrarOtrosUsuarios, borrarUsuario, pedirAlmacenPersistente } from "./almacenOffline";
import { fijarModoActivo, fijarUsuarioOffline, modoActivo, usuarioOffline } from "./estadoOffline";
import { recordarModo } from "./interruptorOffline";
import { vaciarMemoriaEspejo } from "./espejoOffline";
import { cancelarGuardadosPendientes } from "./persistidorOffline";

/**
 * Con red dudosa, cuánto se espera a que la biblioteca de Supabase dé el usuario
 * antes de entrar con la sesión guardada (sin red segura se entra al momento).
 * Por debajo de los 8 s de `AppAuth` y de los 15 s del suelo de `AuthContext`.
 */
export const ESPERA_SESION_GUARDADA_MS = 4000;

export function prepararDatosOffline(uid: string): void {
  // Con el interruptor apagado ni se toca IndexedDB.
  if (!modoActivo()) return;
  pedirAlmacenPersistente();
  void borrarOtrosUsuarios(uid);
}

export async function borrarDatosOffline(uid: string, queryClient?: QueryClient): Promise<void> {
  // Primero se deja de guardar: nada en vuelo puede escribir ya a nombre de nadie.
  cancelarGuardadosPendientes();
  if (usuarioOffline() === uid) fijarUsuarioOffline(null);
  vaciarMemoriaEspejo();
  queryClient?.clear();
  await borrarUsuario(uid);
}

/**
 * El interruptor se APAGÓ para la empresa de este usuario: se deja de guardar y de
 * contestar desde el teléfono, y se borra lo guardado. La caché en memoria NO se
 * vacía (la pantalla que se está viendo sigue igual; lo siguiente ya va por la red).
 */
export async function apagarModoSinConexion(uid: string): Promise<void> {
  fijarModoActivo(false);
  recordarModo(uid, false);
  cancelarGuardadosPendientes();
  vaciarMemoriaEspejo();
  await borrarUsuario(uid);
}

/** El interruptor se ENCENDIÓ: desde ya se guarda y se contesta desde el teléfono. */
export function encenderModoSinConexion(uid: string): void {
  recordarModo(uid, true);
  fijarModoActivo(true);
  prepararDatosOffline(uid);
}
